import { Component, lazy, Suspense, type PropsWithChildren, type ReactNode } from 'react';

const RETRY_COUNT = 5;
const RETRY_INTERVAL_MS = 500;
const MODULE_ERROR_MESSAGE = 'Failed to fetch dynamically imported module:';

interface Props extends PropsWithChildren {
  /** Name of the component. Displayed in the error message when import fails. */
  name: string;

  fallback?: ReactNode;
}

type RetryFn = () => ReturnType<Parameters<typeof lazy>[0]>;

/**
 * A wrapper component that handles loading and error states for lazy-loaded components.
 *
 * Intended to be used with the `retry` function, which attempts to import the component up to
 * `RETRY_COUNT` times before displaying an error message if all attempts fail.
 *
 * ```tsx
 * const Example = lazy(retry(() => import('./Example')));
 * ...
 * return <LazyLoadWrapper name="Example"><Example /></LazyLoadWrapper>
 * ```
 */
export class LazyLoadWrapper extends Component<Props> {
  state = { hasError: false };

  componentDidCatch() {
    this.setState({ hasError: true });
  }

  render() {
    if (!this.state.hasError) {
      return <Suspense fallback={this.props.fallback}>{this.props.children}</Suspense>;
    }

    return <div>Failed to load {this.props.name}</div>;
  }
}

export function retry(fn: RetryFn) {
  return () => retryUntilFailed(fn, RETRY_COUNT);
}

async function retryUntilFailed(fn: RetryFn, retriesLeft: number) {
  try {
    return await fn();
  } catch (error) {
    if (retriesLeft === 1) {
      throw error;
    }

    await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS));

    // if URL can be parsed from error message, retry the import again with different query to bypass browser cache
    const url =
      error instanceof Error &&
      error.message.includes(MODULE_ERROR_MESSAGE) &&
      error.message.replace(MODULE_ERROR_MESSAGE, '').trim();

    if (url) {
      const nextUrl = new URL(url);
      nextUrl.searchParams.set('c', new Date().getTime().toString());

      return retryUntilFailed(() => import(/* @vite-ignore */ nextUrl.href), retriesLeft - 1);
    }

    return retryUntilFailed(fn, retriesLeft - 1);
  }
}
